import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';

interface ItemHash {
  guid: string;
  address: string;
}

export interface InputListSignature {
  Element: HTMLDivElement;
  Args: {
    onChange: (arg0: string[], arg1: boolean) => void;
    values: string[];
  };
  Blocks: {
    default: [
      valuesList: ItemHash[],
      onChangeValue: (arg0: string, arg1: ItemHash) => void,
      onRemove: (arg0: string) => void,
    ];
  };
}
type InputListArgs = InputListSignature['Args'];

const REGEXP_EMAIL =
  /^(([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+([;.](([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+)*$/;

export default class InputList extends Component<InputListSignature> {
  @tracked valuesList: ItemHash[] = [];

  constructor(owner: Owner, args: InputListArgs) {
    super(owner, args);

    const newList = [];
    const item: ItemHash = { guid: '', address: '' };
    item.guid = guidFor(item);
    newList.push(item);
    this.valuesList = newList;
  }

  // Function that adds an item until we get to the max of 10 items.
  withEmptyAtEnd(valuesList: ItemHash[]): ItemHash[] {
    const newList: ItemHash[] = [...valuesList];
    const lastValueList = valuesList[valuesList.length - 1];
    if (
      valuesList.length < 10 &&
      (valuesList.length === 0 || (valuesList.length > 0 && lastValueList && lastValueList.address.trim() !== ''))
    ) {
      const item: ItemHash = { guid: '', address: '' };
      item.guid = guidFor(item);
      newList.push(item);
    }
    return newList;
  }

  // Function that checks for duplicate emails, if there are no duplicates, it returns false.
  checkDuplicateEmails(emailArray: ItemHash[]) {
    const newEmailArray = emailArray.filter(function (item, index, inputArray) {
      return inputArray.indexOf(item) === index;
    });
    return emailArray.length !== newEmailArray.length;
  }

  // Function that checks for valid email values.
  hasInvalids(emailArray: ItemHash[]) {
    return emailArray.some((element) => {
      return element.address.length !== 0 && !REGEXP_EMAIL.test(element.address);
    });
  }

  // Function that returns an array of email addresses
  getEmailList(emailArray: ItemHash[]) {
    const retArr = [];
    for (const email of emailArray) {
      retArr.push(email.address.trim());
    }
    return retArr;
  }

  // Function bound to ValueChange
  @action onChangeValue(_index: string, hash: ItemHash) {
    this.valuesList = this.withEmptyAtEnd(
      this.valuesList
        .map((item) => {
          return item.guid === hash.guid ? hash : item;
        })
        .filter((item) => {
          return item.address !== '';
        }),
    );
    this.args.onChange(this.getEmailList(this.valuesList), this.hasInvalids(this.valuesList));
  }

  // Function bound to Clear/Delete
  @action onRemove(index: string) {
    this.valuesList = this.withEmptyAtEnd(
      this.valuesList.filter((item) => {
        return item.guid !== index;
      }),
    );
    this.args.onChange(this.getEmailList(this.valuesList), this.hasInvalids(this.valuesList));
  }

  @action onFocusOut() {
    const lastValueList = this.valuesList[this.valuesList.length - 1];
    if (lastValueList && lastValueList.address !== '') {
      this.valuesList = this.withEmptyAtEnd(this.valuesList);
    }
  }
}
